// src/components/AdminSettings/modals/FamilyMemberModal.tsx
import React, { useState } from 'react';
import { IonItem, IonLabel, IonToggle } from '@ionic/react';
import { differenceInYears } from 'date-fns';
import { Modal, ModalSection } from '../../ui/Modal';
import { TextInput } from '../../ui/TextInput';
import { FormSelect } from '../../ui/FormSelect';
import { ColorPicker, MEMBER_COLORS } from '../../ui/ColorPicker';
import { AvatarPicker } from '../../ui/AvatarPicker';
import { RELATIONSHIP_SELECT_OPTIONS, MEMBER_TYPE_OPTIONS } from '../constants';
import { getMemberTypeFromBirthDate, getMemberTypeFromRelationship } from '../types';
import type { FamilyMemberModalProps } from '../types';

export const FamilyMemberModal: React.FC<FamilyMemberModalProps> = ({
  isOpen,
  onClose,
  editingMember,
  form,
  onFormChange,
  onSave,
  onDelete,
  userId,
  googleAvatarUrl,
}) => {
  const [relationshipSearch, setRelationshipSearch] = useState('');

  // Calculate age from birth date
  const age = form.birth_date
    ? differenceInYears(new Date(), new Date(form.birth_date))
    : null;

  // Handle birth date change - auto-calculate member type
  const handleBirthDateChange = (dateStr: string | null) => {
    const newMemberType = getMemberTypeFromBirthDate(dateStr);
    onFormChange({
      ...form,
      birth_date: dateStr,
      member_type: newMemberType,
      // Auto-set is_parent to false for children/teens
      is_parent: newMemberType === 'adult' ? form.is_parent : false,
    });
  };

  // Handle relationship change - infer member type if no birth date
  const handleRelationshipChange = (relationship: typeof form.relationship) => {
    const updates: Partial<typeof form> = { relationship };

    // If no birth date, infer member type from relationship
    if (!form.birth_date) {
      updates.member_type = getMemberTypeFromRelationship(relationship);
    }

    onFormChange({ ...form, ...updates });
    setRelationshipSearch('');
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="md"
      header={{ title: editingMember ? 'Edit Family Member' : 'Add Family Member' }}
      footer={{
        primaryAction: {
          label: editingMember ? 'Save' : 'Add Member',
          onClick: onSave,
          disabled: !form.name,
        },
        secondaryAction: { label: 'Cancel', onClick: onClose },
        ...(editingMember && {
          tertiaryAction: {
            label: 'Delete',
            onClick: onDelete,
            variant: 'danger' as const,
          },
        }),
      }}
    >
      <ModalSection spacing="normal">
        <TextInput
          label="Name"
          value={form.name}
          onChange={(v) => onFormChange({ ...form, name: v })}
          placeholder="Full name"
          variant="default"
        />
        <div style={{ marginTop: 16 }}>
          <TextInput
            label="Nickname (optional)"
            value={form.nickname}
            onChange={(v) => onFormChange({ ...form, nickname: v })}
            placeholder="Display name"
            helperText="Shown instead of full name in the app"
            variant="default"
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <FormSelect
            label="Relationship"
            value={relationshipSearch}
            onChange={setRelationshipSearch}
            options={RELATIONSHIP_SELECT_OPTIONS}
            selectedOption={RELATIONSHIP_SELECT_OPTIONS.find(r => r.value === form.relationship) || null}
            onSelect={(opt) => handleRelationshipChange(opt.value)}
            onClear={() => onFormChange({ ...form, relationship: '' })}
            placeholder="Select relationship..."
            variant="default"
          />
        </div>
        <div style={{ marginTop: 16 }}>
          <label style={{
            display: 'block',
            fontFamily: 'var(--kff-data-font)',
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--ion-color-medium)',
            marginBottom: 6,
          }}>
            Birth Date {age !== null && <span style={{ fontWeight: 400 }}>({age} yrs old)</span>}
          </label>
          <input
            type="date"
            value={form.birth_date || ''}
            onChange={(e) => handleBirthDateChange(e.target.value || null)}
            max={new Date().toISOString().split('T')[0]}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: 16,
              fontFamily: 'var(--kff-data-font)',
              border: '1.5px solid var(--ion-color-step-200)',
              borderRadius: 10,
              backgroundColor: 'var(--ion-color-step-50)',
              color: 'var(--ion-text-color)',
              outline: 'none',
            }}
          />
          {form.birth_date && (
            <p style={{
              fontSize: 11,
              color: 'var(--ion-color-medium)',
              marginTop: 4,
              marginBottom: 0,
            }}>
              Auto-detected: {MEMBER_TYPE_OPTIONS.find(t => t.value === form.member_type)?.label || form.member_type}
            </p>
          )}
        </div>
        {/* Member type - shown as read-only when birth date is set, editable otherwise */}
        {!form.birth_date && (
          <div style={{ marginTop: 16 }}>
            <FormSelect
              label="Member Type"
              value=""
              onChange={() => {}}
              options={MEMBER_TYPE_OPTIONS}
              selectedOption={MEMBER_TYPE_OPTIONS.find(t => t.value === form.member_type) || MEMBER_TYPE_OPTIONS[0]}
              onSelect={(opt) => {
                onFormChange({ ...form, member_type: opt.value });
              }}
              placeholder="Select type..."
              variant="default"
              searchable={false}
              clearable={false}
            />
            <p style={{
              fontSize: 11,
              color: 'var(--ion-color-medium)',
              marginTop: 4,
              marginBottom: 0,
            }}>
              Set birth date to auto-calculate
            </p>
          </div>
        )}
        {/* Only show AvatarPicker when adding new members - for existing members, click their avatar in the list */}
        {!editingMember && (
          <div style={{ marginTop: 16 }}>
            <label style={{
              display: 'block',
              fontFamily: 'var(--kff-data-font)',
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--ion-color-medium)',
              marginBottom: 8,
            }}>
              Avatar
            </label>
            <AvatarPicker
              value={form.avatar}
              onChange={(avatar) => onFormChange({ ...form, avatar })}
              userId={userId}
              googleAvatarUrl={googleAvatarUrl}
              storageBucket="avatars"
              defaultTab="icon"
              maxFileSizeMB={2}
            />
          </div>
        )}
        <div style={{ marginTop: 16 }}>
          <ColorPicker
            label="Color"
            value={form.color}
            onChange={(color) => onFormChange({ ...form, color })}
            presetColors={MEMBER_COLORS}
            showCustomInput={true}
            columns={5}
            swatchSize={40}
          />
        </div>
        <IonItem style={{ marginTop: 16, '--background': 'transparent' }}>
          <IonLabel>Is Parent/Admin?</IonLabel>
          <IonToggle
            checked={form.is_parent}
            onIonChange={e => onFormChange({ ...form, is_parent: e.detail.checked })}
          />
        </IonItem>
      </ModalSection>
    </Modal>
  );
};

export default FamilyMemberModal;
